import { useAuth } from '../hooks/useAuth';
import { useMemo } from 'react';

export default function useLivestreamPermissions(livestream) {
  const { user } = useAuth();
  const userId = user?.id;
  const isAdmin = user?.roles?.includes('ADMIN');
  const isSiteOwner = user?.roles?.includes('SITE_OWNER');

  // Memoize permissions to prevent unnecessary recalculations
  return useMemo(() => {
    if (!livestream || !userId) {
      return {

        needsCourtOwnerWarning: false
      };
    }

    // Check if user is the court owner - matches backend validation
    // The backend sends both courtId and full court object with owner info
    const isCourtOwner = livestream.court?.owner?.id === userId;

    // Basic management permissions - matches backend @PreAuthorize logic
    // ADMIN: can manage any livestream
    // SITE_OWNER: can manage livestreams for courts they own
    const canManageLivestream = isAdmin || (isSiteOwner && isCourtOwner);
    
    // Status-based permissions based on backend logic
    const canStartLivestream = (() => {
      if (!canManageLivestream) {

        return false;
      }
      
      // Allow starting if status is SCHEDULED or if we're transitioning to IN_PROGRESS
      if (livestream?.status === 'SCHEDULED' || livestream?.status === 'IN_PROGRESS') {

        return true;
      }
      
      if (livestream?.status === 'COMPLETED' || livestream?.status === 'STOPPED') {
        // Only allow restart if end time hasn't passed - matches backend logic
        const canRestart = livestream?.endTime && new Date(livestream.endTime) > new Date();

        return canRestart;
      }

      return false;
    })();

    // Only allow stopping if in progress and user is the specific court owner
    const canStopLivestream = isCourtOwner && livestream?.status === 'IN_PROGRESS';
    
    // Join permissions - matches backend joinLivestream logic
    // ADMIN: can join any livestream
    // SITE_OWNER: can join livestreams for courts they own
    // Regular USER: can only join if they have purchased access OR it's a free livestream
    const canJoinLivestream = (() => {
      if (livestream?.status !== 'IN_PROGRESS') {
        return false;
      }
      
      // Admin can join any livestream
      if (isAdmin) {
        return true;
      }
      
      // Court owner can join their own livestreams
      if (isSiteOwner && isCourtOwner) {
        return true;
      }
      
      // For regular users, check if they have access
      // A livestream is free if it doesn't require payment OR if it explicitly has a viewersPrice of 0
      // If requiresPayment is true but viewersPrice is null, it should be treated as paid
      const isFreeLivestream = !livestream.requiresPayment || 
        (livestream.viewersPrice !== null && livestream.viewersPrice === 0);
      
      // Check if user has purchased access (handle both string and number IDs)
      const purchaseIds = livestream.liveStreamPurchaseIds ?? [];
      const hasPurchasedAccess = purchaseIds.some(purchaseId => 
        String(purchaseId) === String(userId) || Number(purchaseId) === Number(userId)
      );
      
      console.log('🔍 Permissions - Join Check:', {
        livestreamId: livestream.id,
        userId: userId,
        isFreeLivestream,
        isPaidLivestream: !isFreeLivestream,
        requiresPayment: livestream.requiresPayment,
        price: livestream.price,
        viewersPrice: livestream.viewersPrice,
        priceType: typeof livestream.price,
        viewersPriceType: typeof livestream.viewersPrice,
        priceIsNull: livestream.price === null,
        viewersPriceIsNull: livestream.viewersPrice === null,
        priceIsZero: livestream.price === 0,
        viewersPriceIsZero: livestream.viewersPrice === 0,
        purchaseIds: purchaseIds,
        hasPurchasedAccess,
        canJoin: isFreeLivestream || hasPurchasedAccess,
        userRoles: user?.roles,
        isAdmin,
        isSiteOwner,
        isCourtOwner
      });
      
      // Regular users can only join free livestreams or ones they've purchased
      return isFreeLivestream || hasPurchasedAccess;
    })();
    
    // Only allow the court owner to update scores for their livestream
    const canUpdateScore = isCourtOwner && livestream?.status === 'IN_PROGRESS';
    
    // Viewer status
    const hasJoined = livestream?.viewers?.some(v => v.id === userId) || false;

    // Court owner validation - matches backend validation
    const noCourtOwner = !livestream.court?.owner?.id;
    const needsCourtOwnerWarning = noCourtOwner && livestream.status === 'SCHEDULED';

    // Edit/delete permissions - matches backend authorization
    const canEditLivestream = canManageLivestream && 
      (livestream.status === 'SCHEDULED' || livestream.status === 'POSTPONED' || livestream.status === 'RESCHEDULED');
    const canDeleteLivestream = isAdmin && 
      livestream.status !== 'IN_PROGRESS' && 
      livestream.status !== 'COMPLETED';

    // Payment-related permissions
    const isFreeLivestream = !livestream.requiresPayment || 
      !livestream.price || 
      livestream.price === 0;
    const isPaidLivestream = livestream.requiresPayment && 
      livestream.price && 
      livestream.price > 0;
    // Check if user has purchased access (handle both string and number IDs)
    const purchaseIds = livestream.liveStreamPurchaseIds ?? [];
    const hasPurchasedAccess = purchaseIds.some(purchaseId => 
      String(purchaseId) === String(userId) || Number(purchaseId) === Number(userId)
    );

    return {
      canManageLivestream,
      canEditLivestream,
      canDeleteLivestream,
      canStopLivestream,
      canJoinLivestream,
      canUpdateScore,
      canStartLivestream,
      hasJoined,
      isCourtOwner,
      needsCourtOwnerWarning,
      isFreeLivestream,
      isPaidLivestream,
      hasPurchasedAccess
    };
  }, [livestream, userId, isAdmin, isSiteOwner]);
} 